import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Phone,
  Globe,
  Mail,
  MapPin,
  Clock,
  ArrowLeft,
  ArrowRight,
  Tag,
  ExternalLink,
} from "lucide-react";
import { fetchListing, fetchRelated, CATEGORY_LABELS_I18N } from "@/api/listings";
import ImageWithFallback from "@/components/directory/ImageWithFallback";
import ListingCard from "@/components/directory/ListingCard";
import ShareButtons from "@/components/directory/ShareButtons";
import Seo from "@/components/Seo";
import { LLink } from "@/i18n/LLink";
import { useLang } from "@/i18n/LangContext";

const SITE_URL = process.env.REACT_APP_SITE_URL || "";

const NOT_FOUND_TITLE = { nl: "Bedrijf niet gevonden", de: "Unternehmen nicht gefunden", en: "Business not found" };
const NOT_FOUND_BODY = { nl: "We konden dit bedrijf niet vinden. Ga terug naar de gids en probeer het opnieuw.", de: "Dieses Unternehmen wurde nicht gefunden. Kehren Sie zum Verzeichnis zurück.", en: "We couldn't find this business. Go back to the directory and try again." };
const BEKIJK_OP_KAART = { nl: "Bekijk op de kaart", de: "Auf der Karte ansehen", en: "View on the map" };
const ALLES_IN = { nl: "Alles in", de: "Alle in", en: "All in" };

const categoryAccent = {
  "eten-drinken": "#CE1B2A",
  winkelen: "#2E9CD6",
  diensten: "#173A5E",
  beleven: "#F3C300",
  verblijf: "#6BC0E8",
};

const formatPhoneHref = (phone) => {
  if (!phone) return null;
  const cleaned = phone.replace(/[^\d+]/g, "");
  return `tel:${cleaned}`;
};

const ensureUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
};

const NotFound = () => {
  const { t } = useTranslation();
  const { lang } = useLang();
  return (
    <section className="ll-section-soft py-20">
      <div className="ll-container max-w-2xl text-center">
        <h1 className="text-4xl font-bold mb-4">{NOT_FOUND_TITLE[lang] || NOT_FOUND_TITLE.nl}</h1>
        <p className="text-[#3E5871] font-[Inter] mb-6">{NOT_FOUND_BODY[lang] || NOT_FOUND_BODY.nl}</p>
        <LLink to="/bedrijvengids" className="ll-btn-primary">
          <ArrowLeft className="w-4 h-4" />
          {t("common.terug_naar_gids")}
        </LLink>
      </div>
    </section>
  );
};

const LoadingSkeleton = () => (
  <div className="ll-container py-12">
    <div className="h-6 w-40 bg-[#F4F8FB] rounded animate-pulse mb-6" />
    <div className="h-10 w-2/3 bg-[#F4F8FB] rounded animate-pulse mb-3" />
    <div className="h-4 w-1/2 bg-[#F4F8FB] rounded animate-pulse mb-8" />
    <div className="aspect-[16/9] bg-[#F4F8FB] rounded-2xl animate-pulse" />
  </div>
);

export const BedrijfDetailPage = () => {
  const { slug } = useParams();
  const { t } = useTranslation();
  const { lang } = useLang();
  const [listing, setListing] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setListing(null);
    setRelated([]);
    (async () => {
      try {
        const l = await fetchListing(slug);
        if (cancelled) return;
        setListing(l);
        // Bepaal primaire categorie (zelfde bron als de heading) zodat kop en inhoud altijd matchen
        const primary =
          (l.categories && l.categories.length > 0 ? l.categories[0] : null) ||
          l.category ||
          null;
        const r = primary
          ? await fetchRelated(slug, 3, primary).catch(() => [])
          : [];
        if (cancelled) return;
        setRelated(r);
      } catch (e) {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    window.scrollTo(0, 0);
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) return <LoadingSkeleton />;
  if (notFound || !listing) return <NotFound />;

  const accent = categoryAccent[listing.category] || "#2E9CD6";
  const displayCats = listing.categories && listing.categories.length > 0
    ? listing.categories
    : (listing.category ? [listing.category] : []);
  // Primaire categorie = eerste uit categories-array (nieuw multi-cat model) → fallback op legacy category-veld.
  // Deze wordt gebruikt voor de "Meer in deze categorie <X>"-sectie, de eyebrow-label
  // en de "Alles in X" link. Backend krijgt dezelfde waarde mee zodat kop en content matchen.
  const primaryCategory = displayCats[0] || listing.category || "";
  const catLabel = CATEGORY_LABELS_I18N[primaryCategory]?.[lang]
    || CATEGORY_LABELS_I18N[primaryCategory]?.nl
    || primaryCategory;
  const hasDescription = !!listing.description_nl?.trim();
  const phoneHref = formatPhoneHref(listing.phone);
  const websiteHref = ensureUrl(listing.website);
  const emailHref = listing.email ? `mailto:${listing.email}` : null;
  const fullAddress = [listing.address, listing.postcode, listing.city]
    .filter(Boolean)
    .join(", ");
  const hasAddress = !!listing.address?.trim();
  const gmapsHref = hasAddress
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        [listing.name, listing.address, [listing.postcode, listing.city].filter(Boolean).join(" ")]
          .filter(Boolean)
          .join(", "),
      )}`
    : null;

  // JSON-LD LocalBusiness voor zoekmachines die JS uitvoeren.
  const canonicalPath = `${lang === "nl" ? "" : "/" + lang}/bedrijf/${listing.slug}`;
  const pageUrl = `${SITE_URL}${canonicalPath}`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": listing.name,
    "url": pageUrl,
    ...(listing.description_nl && { description: listing.description_nl }),
    ...(listing.image && { image: listing.image.startsWith("http") ? listing.image : `${SITE_URL}${listing.image}` }),
    ...(fullAddress && {
      address: {
        "@type": "PostalAddress",
        streetAddress: listing.address || "",
        postalCode: listing.postcode || "",
        addressLocality: listing.city || "Lemmer",
        addressCountry: "NL",
      },
    }),
    ...(listing.lat != null && listing.lng != null && {
      geo: { "@type": "GeoCoordinates", latitude: listing.lat, longitude: listing.lng },
    }),
    ...(listing.phone && { telephone: listing.phone }),
    ...(listing.email && { email: listing.email }),
    ...(listing.website && { sameAs: [ensureUrl(listing.website)].filter(Boolean) }),
  };

  return (
    <div data-testid="bedrijf-detail-page" className="bg-white">
      <Seo
        title={`${listing.name} — Lemmer Leeft`}
        description={listing.description_nl || `${listing.name} in Lemmer`}
        lang={lang}
        image={listing.image}
        jsonLd={jsonLd}
      />
      {/* Terug-link */}
      <div className="ll-section-soft border-b border-[#E4EEF5]">
        <div className="ll-container py-4">
          <LLink
            to="/bedrijvengids"
            data-testid="back-to-gids"
            className="inline-flex items-center gap-1.5 text-sm font-[Poppins] font-semibold text-[#2E9CD6] hover:text-[#173A5E] transition"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("common.terug_naar_gids")}
          </LLink>
        </div>
      </div>

      {/* Header + image */}
      <section className="py-8 sm:py-12">
        <div className="ll-container">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-10">
            {/* Foto */}
            <div className="lg:col-span-2">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-md bg-[#F4F8FB]">
                <ImageWithFallback
                  src={listing.image}
                  alt={listing.name}
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>
            </div>

            {/* Info */}
            <div className="lg:col-span-3 flex flex-col">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {displayCats.map((cat, idx) => {
                  const catAcc = categoryAccent[cat] || "#2E9CD6";
                  const label = CATEGORY_LABELS_I18N[cat]?.[lang] || CATEGORY_LABELS_I18N[cat]?.nl || cat;
                  return (
                    <span
                      key={cat}
                      data-testid={idx === 0 ? "detail-category-tag" : `detail-category-tag-${cat}`}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-[Poppins] font-semibold"
                      style={{
                        backgroundColor: catAcc,
                        color: catAcc === "#F3C300" ? "#173A5E" : "#FFFFFF",
                      }}
                    >
                      <Tag className="w-3 h-3" />
                      {label}
                    </span>
                  );
                })}
                {listing.soort && (
                  <span className="px-3 py-1 rounded-full text-xs font-[Poppins] font-semibold bg-[#F4F8FB] text-[#173A5E] border border-[#E4EEF5]">
                    {listing.soort}
                  </span>
                )}
                {listing.membership && listing.membership !== "???" && (
                  <span className="px-3 py-1 rounded-full text-xs font-[Poppins] font-semibold bg-[#6BC0E8]/20 text-[#173A5E]">
                    {listing.membership}
                  </span>
                )}
              </div>

              <h1
                data-testid="detail-name"
                className="text-4xl sm:text-5xl font-bold mb-4"
              >
                {listing.name}
              </h1>

              {fullAddress && (
                <p className="text-[#3E5871] font-[Inter] text-base mb-2 flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-1 shrink-0 text-[#2E9CD6]" />
                  {fullAddress}
                </p>
              )}

              {listing.opening_hours && (
                <p className="text-[#3E5871] font-[Inter] text-base mb-2 flex items-start gap-2">
                  <Clock className="w-4 h-4 mt-1 shrink-0 text-[#2E9CD6]" />
                  <span className="whitespace-pre-line">
                    {listing.opening_hours}
                  </span>
                </p>
              )}

              {/* Beschrijving */}
              <div className="mt-4 mb-6">
                {hasDescription ? (
                  <p
                    data-testid="detail-description"
                    className="text-[#3E5871] font-[Inter] text-base sm:text-lg leading-relaxed whitespace-pre-line"
                  >
                    {listing.description_nl}
                  </p>
                ) : (
                  <p
                    data-testid="detail-description-placeholder"
                    className="text-[#5A7594] font-[Inter] italic text-base leading-relaxed"
                  >
                    {t("common.binnenkort_meer")}.
                  </p>
                )}
              </div>

              {/* Contact-knoppen */}
              <div className="flex flex-wrap gap-3 mt-auto">
                {phoneHref && (
                  <a
                    href={phoneHref}
                    data-testid="detail-btn-phone"
                    className="ll-btn-primary"
                  >
                    <Phone className="w-4 h-4" />
                    {t("common.bel")} {listing.phone}
                  </a>
                )}
                {websiteHref && (
                  <a
                    href={websiteHref}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="detail-btn-website"
                    className="ll-btn-accent"
                  >
                    <Globe className="w-4 h-4" />
                    {t("common.website")}
                  </a>
                )}
                {emailHref && (
                  <a
                    href={emailHref}
                    data-testid="detail-btn-email"
                    className="ll-btn-outline"
                  >
                    <Mail className="w-4 h-4" />
                    {t("common.email")}
                  </a>
                )}
                {gmapsHref && (
                  <a
                    href={gmapsHref}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid="detail-btn-gmaps"
                    aria-label={BEKIJK_OP_KAART[lang] || BEKIJK_OP_KAART.nl}
                    className="ll-btn-outline"
                  >
                    <MapPin className="w-4 h-4" />
                    {BEKIJK_OP_KAART[lang] || BEKIJK_OP_KAART.nl}
                    <ExternalLink className="w-3.5 h-3.5 opacity-70" />
                  </a>
                )}
              </div>

              {/* Deel deze pagina */}
              <ShareButtons name={listing.name} slug={listing.slug} />
            </div>
          </div>
        </div>
      </section>

      {/* Gerelateerd */}
      {related.length > 0 && (
        <section className="ll-section-soft py-12 sm:py-16">
          <div className="ll-container">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
              <div>
                <span className="inline-block text-[#2E9CD6] font-[Poppins] font-semibold text-xs uppercase tracking-[0.2em] mb-2">
                  {catLabel}
                </span>
                <h2 className="text-2xl sm:text-3xl font-bold">
                  {t("common.meer_in_categorie")}
                </h2>
              </div>
              <LLink
                to={`/bedrijvengids?category=${primaryCategory}`}
                data-testid="see-all-in-category"
                className="ll-btn-outline"
              >
                {ALLES_IN[lang] || ALLES_IN.nl} {catLabel}
                <ArrowRight className="w-4 h-4" />
              </LLink>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((r) => (
                <ListingCard key={r.slug} listing={r} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BedrijfDetailPage;
